import styled from 'styled-components';
import Inner from '../components/Inner';
import PageTitle from '../components/PageTitle';
import { styled as muiStyled } from '@mui/system';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button/Button';
import SignAlert from '../components/SignAlert';
import { useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import { useNavigate } from 'react-router-dom';
import { addUser } from '../store/usersSlice';

const Contents = styled.div`
	margin: 0 auto;
`;

const Inputs = styled.div`
	width: 22rem;
	margin: 0 auto;

	@media (max-width: 767px) {
		width: fit-content;
	}
`;

const CustomButton = muiStyled(Button)({
	fontSize: '1.4rem',
	fontWeight: '700',
	marginTop: 35,
});

function SignUp() {
	const idInputRef = useRef<HTMLInputElement>(null);
	const pwInputRef = useRef<HTMLInputElement>(null);
	const pwCheckInputRef = useRef<HTMLInputElement>(null);

	const users = useAppSelector((state) => {
		return state.users.users;
	});
	const dispatch = useAppDispatch();

	const navigate = useNavigate();

	const [errors, setErrors] = useState({
		isIdInputEmpty: false,
		isPwInputEmpty: false,
		isIdExist: false,
		isPwCheckFail: false,
	});

	const [inputs, setInputs] = useState({
		id: '',
		pw: '',
		pwCheck: '',
	});

	const { id, pw, pwCheck } = inputs;

	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, name } = e.target;

		setInputs({
			...inputs,
			[name]: value,
		});
	};

	const handleSubmit = (e: React.SyntheticEvent) => {
		e.preventDefault();

		const initialErrors = {
			isIdInputEmpty: false,
			isPwInputEmpty: false,
			isIdExist: false,
			isPwCheckFail: false,
		};

		// 아이디를 입력하지 않은 경우
		if (id === '') {
			idInputRef.current?.focus();
			setErrors({ ...initialErrors, isIdInputEmpty: true });
		}
		// 비밀번호를 입력하지 않은 경우
		else if (pw === '') {
			pwInputRef.current?.focus();
			setErrors({ ...initialErrors, isPwInputEmpty: true });
		} else {
			e.preventDefault();

			const checkInputId = users.find((user) => user.id === id);

			// 이미 사용 중인 아이디인 경우
			if (checkInputId) {
				idInputRef.current?.focus();
				setErrors({ ...initialErrors, isIdExist: true });
			} else {
				// 비밀번호 확인 실패한 경우
				if (pw !== pwCheck) {
					pwCheckInputRef.current?.focus();
					setErrors({ ...initialErrors, isPwCheckFail: true });
				}
				// 회원가입 성공
				else {
					setInputs({
						id: '',
						pw: '',
						pwCheck: '',
					});
					setErrors(initialErrors);
					dispatch(addUser({ id: id, pw: pw, cart: [] }));
					return navigate('/signin');
				}
			}
		}
	};

	const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleSubmit(e);
		}
	};

	return (
		<Inner>
			<PageTitle korean>회원가입</PageTitle>
			<Contents>
				<Inputs>
					<TextField
						name='id'
						inputRef={idInputRef}
						onChange={handleInput}
						onKeyDown={handleEnter}
						value={id}
						margin='normal'
						label='아이디'
						fullWidth
						autoFocus
					/>
					<TextField
						name='pw'
						inputRef={pwInputRef}
						onChange={handleInput}
						onKeyDown={handleEnter}
						value={pw}
						margin='normal'
						label='비밀번호'
						type='password'
						fullWidth
					/>
					<TextField
						name='pwCheck'
						inputRef={pwCheckInputRef}
						onChange={handleInput}
						onKeyDown={handleEnter}
						value={pwCheck}
						margin='normal'
						label='비밀번호 확인'
						type='password'
						fullWidth
					/>
					<CustomButton
						onClick={handleSubmit}
						type='submit'
						fullWidth
						variant='contained'
					>
						회원가입하기
					</CustomButton>
					{errors.isIdInputEmpty && (
						<SignAlert severity='error'>아이디를 입력해주세요.</SignAlert>
					)}
					{errors.isPwInputEmpty && (
						<SignAlert severity='error'>비밀번호를 입력해주세요.</SignAlert>
					)}
					{errors.isIdExist && (
						<SignAlert severity='error'>이미 사용 중인 아이디입니다.</SignAlert>
					)}
					{errors.isPwCheckFail && (
						<SignAlert severity='error'>비밀번호가 일치하지 않습니다.</SignAlert>
					)}
				</Inputs>
			</Contents>
		</Inner>
	);
}

export default SignUp;
